import { LeadDto } from '@generatedTypes/data-contracts';
import { NullableToUndefindableObject } from '@utils/types/NullableToUndefindable';
import { useGetLead } from '@services/api/leads/lead-info';
import { useUpdateLead } from '@services/api/leads/lead-edit';
import { LeadForm } from './LeadForm/LeadForm';
import { useAnalytics } from '@hooks/useAnalytics/useAnalytics';
import { AnalyticEvents } from '@hooks/useAnalytics/analyticEvents';
import { useQueryParamsModal } from '@utils/navigation/queryParamsNavigation';
import { useCreateLead } from '@services/api/leads/lead-create';
import { LeadZodSchema } from '@pages/NewLeads/LeadForm/zodSettings';
import { useSelector } from 'react-redux';
import { selectSelectedDealerId } from '@redux/reducers/slices/dealerUser';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';

export const AddEditLeadForm = () => {
  const pushDataLayer = useAnalytics();
  const { options, closeModal } = useQueryParamsModal(`lead`);
  const navigate = useNavigate();

  const { lead } = useGetLead();

  const selectedDealerId = useSelector(selectSelectedDealerId);
  const { updateLeadAsync, isLoading, validationErrors: updateValidationErrors } = useUpdateLead();
  const { createLead, validationErrors: createValidationErrors } = useCreateLead();

  const onCreateSubmit = async (data: LeadZodSchema) => {
    pushDataLayer({ event: AnalyticEvents.createLead });
    const createdId = await createLead({ ...data, assignedDealerId: selectedDealerId });
    navigate(`/leads/${createdId}`);
  };

  const onUpdateSubmit = (data: LeadZodSchema) => {
    pushDataLayer({ event: AnalyticEvents.customerInfo, customerInfo: data.customerType });
    return updateLeadAsync({ ...data, id: lead?.id ?? -1 }).then(closeModal);
  };

  const initialValues = useMemo(() => {
    if (options.mode === `edit` && lead) {
      const { address, ...restData } = lead as NullableToUndefindableObject<LeadDto> & {
        address: NullableToUndefindableObject<LeadDto[`address`]>;
      };
      return { ...address, ...restData };
    }
    return undefined;
  }, [lead, options.mode]);

  const onSubmit = options.mode === `edit` ? onUpdateSubmit : onCreateSubmit;

  return (
    <LeadForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      isSubmitting={isLoading}
      errors={createValidationErrors ?? updateValidationErrors}
    />
  );
};
